import { Component, OnInit, EventEmitter, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { StorageService } from '../../shared/services/storage.service';
import { StringHelper } from '../../shared/utils/string-helper';
import {
  IBookmarkListItem,
  RoomReservationBookmarksComponent
} from './components/room-reservation-bookmarks/room-reservation-bookmarks.component';
import { BuildingService } from '../../shared/services/rest/building.service';
import { Building } from '../../shared/models/building.model';
import { RoomService, IAvailableRoomsParams } from '../../shared/services/rest/room.service';
import { RoomDropdown } from '../../shared/models/room-dropdown.model';
import { AvailableRoom } from '../../shared/models/available-room';
import { Occupancy } from '../../shared/models/occupancy';
import { AppConfigService } from 'src/app/shared/services/app-config.service';
import { ArrayHelper } from 'src/app/shared/utils/array-helper';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'src/app/shared/services/message.service';
import { MessageType } from 'src/app/shared/models/message';
import { UrlHelper } from 'src/app/shared/utils/url-helper';
import { DateHelper } from 'src/app/shared/utils/date-helper';
import { IRoomScheduleInfo } from './components/room-allocation-table/room-allocation-table.component';
import { IMultiDropdownData, FORM_VALUE_SEPARATOR } from 'src/app/shared/components/multi-dropdown/multi-dropdown.component';
import { ISearchUrlParams } from 'src/app/shared/models/isearch-url-params';
import { BaseReservationComponent, AvailabilityType } from './base-reservation.component';
import { SearchForm } from './models/search-form';
import { IRoomSearchForm } from './models/i-room-search-form';
import { AllocationTableModel } from './components/room-allocation-table/room-allocation-desktop.component';
import { SessionSettings } from 'src/app/shared/models/client-settings.model';
import { ResourceType } from 'src/app/shared/models/resource-type';
import { UserSearchTermsService } from 'src/app/shared/services/user-search-terms.service';
// import { $ } from 'protractor';
import { interval, merge } from 'rxjs';
import { debounce, first, map, buffer } from 'rxjs/operators';

@Component({
  selector: 'app-room-reservation',
  templateUrl: './room-reservation.component.html',
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomReservationComponent extends BaseReservationComponent<IRoomSearchForm> implements OnInit {

  protected room: RoomService;

  public isSearchPanelExpaneded = true;

  get resourceType(): ResourceType {
    return ResourceType.Raum;
  }

  private initialRun = true;

  /**
   * constant panel id for search panel
   *
   * @memberof RoomReservationComponent
   */
  readonly accordionSearchPanel = 'accordionSearchPanel';

  /**
   * constant panel id for result panel
   *
   * @memberof RoomReservationComponent
   */
  readonly accordionResultPanel = 'accordionResultPanel';

  @ViewChild('bookmarkComponent') private _bookmarkSaveDialog: RoomReservationBookmarksComponent;

  /**
  * Angular Form model
  *
  * @type {FormGroup}
  * @memberof RoomReservationComponent
  */
  searchFormGroup: FormGroup;

  /**
   * indicate whether the infotext is collapsed or not
   *
   * @type {boolean}
   * @memberof RoomReservationComponent
   */
  isCollapsedInfoText: boolean;

  /**
   * Buildings as loaded from rest-api
   *
   * @type {Building[]}
   * @memberof RoomReservationComponent
   */
  buildingsRaw: Building[] = [];

  /**
   * Buildings dropdown-model
   *
   * @type {IMultiDropdownData[]}
   * @memberof RoomReservationComponent
   */
  buildings: IMultiDropdownData[] = [];

  /**
   * Locations dropdown-model
   *
   * @type {IMultiDropdownData[]}
   * @memberof RoomReservationComponent
   */
  locations: IMultiDropdownData[] = [];

  /**
   * RoomType dropdown-model
   *
   * @type {IMultiDropdownData[]}
   * @memberof RoomReservationComponent
   */
  roomType: IMultiDropdownData[] = [];

  /**
   * Floor dropdown-model
   *
   * @type {IMultiDropdownData[]}
   * @memberof RoomReservationComponent
   */
  floor: IMultiDropdownData[] = [];

  /**
   * Number of People dropdown-model
   *
   * @type {IMultiDropdownData[]}
   * @memberof RoomReservationComponent
   */
  seats: IMultiDropdownData[] = [];

  /**
   * Room equipment dropdown-model
   *
   * @type {IMultiDropdownData[]}
   * @memberof RoomReservationComponent
   */
  equipment: IMultiDropdownData[] = [];

  addNewSavedSearch = new EventEmitter<IBookmarkListItem>();

  allocations: AvailableRoom[] = [];

  roomDropDownData: RoomDropdown[];

  detailSearchEnabled: boolean;

  searchTermsSvc: UserSearchTermsService;

  /**
   * id (idAnlass) of the reservation to show in confirmation dialog
   *
   * @type {number}
   * @memberof RoomReservationComponent
   */
  confirmReservationId: number;

  private enableSubSelectors: boolean;

  protected isExpanded = true;

  constructor(
    storage: StorageService,
    fb: FormBuilder,
    building: BuildingService,
    room: RoomService,
    router: Router,
    activatedRoute: ActivatedRoute,
    translate: TranslateService,
    messages: MessageService,
    searchTerms: UserSearchTermsService,
    protected readonly cdr: ChangeDetectorRef,
  ) {
    super(storage, fb, building, router, activatedRoute, translate, messages, cdr);
    this.room = room;
    this.detailSearchEnabled = false;
    this.searchTermsSvc = searchTerms;
  }

  /**
   * Map Building to IMultiDropdownData
   *
   * @param {Building} building
   * @param {boolean} isSelected
   * @returns {IMultiDropdownData}
   * @memberof RoomReservationComponent
   */
  static mapDropDown(building: Building): IMultiDropdownData {
    return { key: String(building.Id), text: building.Designation, selected: false };
  }

  ngOnInit() {
    // ensure scope
    if (AppConfigService.settings.appScope === 'Public') {
      const msg = 'appconfig.json: appScope "Public" is not allowed for this module';
      this.messages.add(msg, MessageType.Error);
      throw new Error(msg);
    }

    // apply appConfig settings
    this.applyUserSettings();

    this.storage.clientSettings.subscribe((data) => {
      this.isCollapsedInfoText = data.roomSearchComponent.infoTextCollapsed;
    });

    // restore settings from session-storage
    this.restoreSessionData();

    this.attachEvents();

    this.loadBuildings();

    // send current data captured in form on demand
    this.searchTermsSvc.formDataRequest.subscribe(() =>{
      return this.searchTermsSvc.formDataResponse.next(this.searchFormGroup.value);
    });
    // override loaded search term of form data
    this.searchTermsSvc.applyFormData.subscribe(formData => {

      return this.runAfterApplyingFavorites(formData);
    });

    // restore settings from user settings endpoint
    this.searchTermsSvc.requestLastSearchTerms().subscribe(data => {

      // ! BUG: Workaround for unexplained different return types
      if (data.status && data.body) { data = data.body; }

      // abort when nothing is yet saved
      if (data.Settings.length === 0) { return; }

      const [lastSearchTerms] = data.Settings;
      const formData = JSON.parse(lastSearchTerms.Value);
      setTimeout(() => {this.searchFormGroup.patchValue(formData)}, 42);
    }, err => {
      console.error('Error: User result data load', err);
    });
  }

  onAttachEvents() {
    // date
    this.searchForm.get('date').valueChanges.subscribe((value: string) => {
      if (value) {
        this.currentDate = DateHelper.parseIsoDateString(value);
      }
    });

    // type
    this.searchForm.get('showAvailableType').valueChanges.subscribe((value: AvailabilityType) => {
      this.updateAvailableTypeState(value);
    });

    // time from
    this.searchForm.get('showAvailableTimeFrom').valueChanges.subscribe((_: number) => { this.updateTime(); });
    // location
    const locationChangeEmitter = this.searchForm.get('location').valueChanges.pipe(map(() => 'location'));
    // buildings
    const buildingChangeEmitter = this.searchForm.get('building').valueChanges.pipe(map(() => 'building'));
  
    // react to only once from short consecetive change emissions
    const base = merge(locationChangeEmitter, buildingChangeEmitter);
    base.pipe(buffer(base.pipe(debounce(() => interval(45)))))
      .subscribe((changedFields: string[]) => {
        // If location changed, update all available buildings
        if (changedFields.includes('location')) {
          this.updateBuildings();
        }
        // always update dropdown if either subscribed fields changes
        this.loadRoomDropdownData();

        this.updateFormState();
    });

    // safe this settings to session-storage to prevent loss on reload
    this.searchTermsSvc.beforeFormSubmit.subscribe(formData => {
      this.searchTermsSvc.storeLastSearchTerms(formData).subscribe(() => { },
        err => {
          console.error('failed to store last search terms', err);
        });
    });

  }

  /**
   * Apply values from user settings file to form
   * 
   * @memberof RoomReservationComponent
   */
  applyUserSettings() {

    const now = DateHelper.getCurrentDate();

    // compute and set some selection defaults
    const defaultTimePeriod = AppConfigService.settings.DefaultTimePeriod;
    const defaultTimeFragmentation = AppConfigService.settings.DefaultTimeFragmentation;
    const FALLBACK_FRAGMENTATION_PERIOD_VALUE = 15;

    // if number does not fit well into an hour, discard and use fallback fragmentation time instead
    const validTimeFragmentation = Number.isInteger(60 / defaultTimeFragmentation)
      ? defaultTimeFragmentation
      : FALLBACK_FRAGMENTATION_PERIOD_VALUE;

    const nextCycleStep = (Math.ceil((now.getMinutes() + 1) / validTimeFragmentation) * validTimeFragmentation);
    const availableStartTimeCountdown = ((now.getHours() + Math.floor(nextCycleStep / 60)) * 60) + (nextCycleStep % 60);

    this.searchForm.setValue('date', now);
    this.searchForm.setValue('showAvailableTimeFrom', availableStartTimeCountdown);
    this.searchForm.setValue('showAvailableTimeTo', availableStartTimeCountdown + defaultTimePeriod);
    this.searchForm.setValue('showAvailableHours', defaultTimePeriod);
  }

  get selectedBuildings(): string[] {
    return this.getFavoritesSelectedBuildings();
  }

  getFavoritesSelectedBuildings(favorite = {}) {
    const value = 'building' in favorite 
      ? favorite['building']
      : this.searchForm.getValue('building');

    return StringHelper.isNullOrEmpty(value) ? [] : value.split(FORM_VALUE_SEPARATOR) as Array<string>;
  }

  /**
   * Locations from form
   *
   * @readonly
   * @type {string[]}
   * @memberof RoomReservationComponent
   */
  get selectedLocations(): string[] {
    return this.getFavoritesSelectedLocations();
  }

  getFavoritesSelectedLocations(favorite = {}) {
    const value = 'location' in favorite 
      ? favorite['location']
      : this.searchForm.getValue('location');

    return StringHelper.isNullOrEmpty(value) ? [] : value.split(FORM_VALUE_SEPARATOR) as Array<string>;
  }

  protected createForm() {
    const formGroup = this.fb.group({ // <-- the parent FormGroup
      number: [''],
      idRoom: '',
      date: [null as Date, Validators.required],
      showExtendedSearch: false,
      location: '',
      building: '',
      roomType: ['', { disable: !this.detailSearchEnabled }],
      floor: '',
      equipments: '',
      seats: '',
      showAvailable: AppConfigService.settings.ShowAvailableRoomsChecked,
      showAvailableType: AvailabilityType.absolut,
      showAvailableHours: 0,
      showAvailableTimeFrom: 0,
      showAvailableTimeTo: 0
    });

    this.searchForm = new SearchForm(formGroup);
    this.searchFormGroup = this.searchForm.formGroup;

    this.updateAvailableTypeState(null);

    this.updateFormState();
  }

  updateFormState() {
    const newState = this.selectedBuildings.length > 0 || this.selectedLocations.length > 0;
    if (newState === this.enableSubSelectors) {
      return;
    }

    this.enableSubSelectors = newState;
    if (this.enableSubSelectors) {
      this.searchForm.enable('roomType');
      this.searchForm.enable('floor');
      this.searchForm.enable('equipments');
      this.searchForm.enable('seats');
    } else {
      this.searchForm.disable('roomType');
      this.searchForm.setValue('roomType', '');

      this.searchForm.disable('floor');
      this.searchForm.setValue('floor', '');

      this.searchForm.disable('equipments');
      this.searchForm.setValue('equipments', '');

      this.searchForm.disable('seats');
      this.searchForm.setValue('seats', '');
    }
  }

  protected OnAfterRestoreSessionData() {
    this.updateAvailableTypeState(null);
  }

  protected getSessionSettingsKey(): keyof SessionSettings {
    return 'roomSearchForm';
  }

  /**
   * load Buildings from server and init location and building dropdowns
   *
   * @memberof RoomReservationComponent
   */
  private loadBuildings() {
    return this.building.getWithRooms().pipe(first()).subscribe(
      (buildings: Array<any>) => {
        this.buildingsRaw = buildings.map(b => Building.create(b));

        // distinct sites from buildings
        const siteIds = new Array<number>();
        const newSites = new Array<IMultiDropdownData>();
        this.buildingsRaw.forEach(b => {
          if (!ArrayHelper.contains(siteIds, b.SiteId)) {
            siteIds.push(b.SiteId);
            newSites.push({ key: String(b.SiteId), text: b.Site, selected: false });
          }
        });

        this.locations = newSites.sortByProp(s => s.text);
        this.updateBuildings();
        this.applyPrimaryUrlParams();
      },
    );
  }

  /**
   * set buildings according to the selected sites
   *
   * @memberof RoomReservationComponent
   */
  updateBuildings(selectedLocations?: string[]) {
    const _selectedLocations = selectedLocations || this.selectedLocations;
    // set available building
    this.buildings = (
      _selectedLocations.length === 0
      ? this.buildingsRaw.map(b => // all
        RoomReservationComponent.mapDropDown(b))
      : this.buildingsRaw // by selected location
        .filter(b => _selectedLocations.includes(String(b.SiteId)))
        .map(b => RoomReservationComponent.mapDropDown(b))
    ).sortByProp(x => x.text);
  }

  runAfterApplyingFavorites(formData) {
    const allBuildingsLocation = this.buildingsRaw.map(b => String(b.SiteId));
    const locations = this.getFavoritesSelectedLocations(formData).filter(l => allBuildingsLocation.includes(l));
    const allBuildingsInLocation = this.buildingsRaw.filter(b => locations.includes(String(b.SiteId))).map(b => String(b.Id));
    const buildings = this.getFavoritesSelectedBuildings(formData).filter(b => allBuildingsInLocation.includes(b));
    
    this.cdr.detach();
    return Promise.resolve()
      .then(() => this.updateBuildings(locations))
      .then(() => this.room.getRoomDropDownData(buildings).toPromise())
      .then(ddd => this.roomDropDownData = ddd)
      .then(() => this.updateFilterDropdowns())
      .then(() => this.searchFormGroup.patchValue(formData))
      .then(() => this.updateFormState())
      .finally(() => {
        this.cdr.reattach();
        this.cdr.detectChanges();
      });
  }

  /**
   * Load filter-dropdown from server
   *
   * @memberof RoomReservationComponent
   */
  loadRoomDropdownData(selectedBuildings?: string[]) {
    const _selectedBuildings = (selectedBuildings || this.selectedBuildings);
    // selected or all
    const buildings = _selectedBuildings.length ? _selectedBuildings : this.buildings.map(b => b.key);
    // load from server
    this.room.getRoomDropDownData(buildings).pipe(first()).subscribe(
      (x: RoomDropdown[]) => {
        this.roomDropDownData = x;

        if (this.initialRun) { this.applySecondaryUrlParams(); }

        this.updateFilterDropdowns();
      }
    );
  }

  /**
   * Update the data in the following dropdowns:
   * Roomtype, Floor, Infrastructure, Number of People
   *
   * @memberof RoomReservationComponent
   */
  updateFilterDropdowns() {
    // room type
    this.roomType = this.roomDropDownData.map(dd => {
      return {
        key: String(dd.IDRoomType),
        text: dd.RoomType,
        selected: false
      };
    })
      .distinctByProp(e => e.key)
      .sortByProp(p => p.text);

    // floor
    this.floor = this.roomDropDownData
      .filter(f => StringHelper.hasAny(f.Floor))
      .map(f => {
        return {
          Floor: f.Floor,
          Sort: StringHelper.hasAny(f.FloorSort) ? f.FloorSort : '  ' + f.Floor
        };
      })
      .distinctByProp(f => f.Floor)
      .sortByProp(f => f.Sort)
      .map(f => {
        return { key: f.Floor, text: f.Floor, selected: false };
      });

    // infra
    this.equipment = this.roomDropDownData.map(e => {
      return {
        key: e.IDRoomEquipment,
        text: e.RoomEquipment,
        selected: false
      };
    })
      .filter(x => StringHelper.hasAny(x.text))
      .distinctByProp(x => x.key)
      .sortByProp(x => x.text);

    // number of people
    this.seats = this.roomDropDownData
      .map(x => x.NumberPersons)
      .distinct()
      .filter(n => n !== null && n > 0)
      .sortNumeric()
      .map(i => {
        return {
          key: String(i),
          text: String(i),
          selected: false
        };
      });
  }

  updateAvailableTypeState(availableType: string) {
    const type = availableType ? availableType : this.searchForm.getValue('showAvailableType');
    const showAvailableHours = this.searchForm.get('showAvailableHours');
    const showAvailableTimeFrom = this.searchForm.get('showAvailableTimeFrom');
    const showAvailableTimeTo = this.searchForm.get('showAvailableTimeTo');

    if (type === AvailabilityType.absolut) {
      showAvailableHours.disable();
      showAvailableTimeFrom.enable();
      showAvailableTimeTo.enable();
      showAvailableHours.validator = null;
      showAvailableTimeFrom.validator = Validators.required;
      showAvailableTimeTo.validator = Validators.required;
    } else {
      // span
      showAvailableHours.enable();
      showAvailableTimeFrom.disable();
      showAvailableTimeTo.disable();
      showAvailableHours.validator = Validators.required;
      showAvailableTimeFrom.validator = null;
      showAvailableTimeTo.validator = null;
    }
  }

  toggleInfoTextCollapse() {
    this.isCollapsedInfoText = !this.isCollapsedInfoText;
    this.storage.updateClientSettings((settings) => {
      settings.roomSearchComponent.infoTextCollapsed = this.isCollapsedInfoText;
    });
  }

  /**
   * Validate selected times and set timeTo according to timeFrom
   *
   * @memberof RoomSearchComponent
   */
  updateTime(): void {
    const from = Number(this.searchForm.getValue('showAvailableTimeFrom'));
    const to = Number(this.searchForm.getValue('showAvailableTimeTo'));
    const defaultTimePeriod = AppConfigService.settings.DefaultTimePeriod;

    if (from >= to) {
      this.searchForm.setValue('showAvailableTimeTo', String(from + defaultTimePeriod));
    }
  }

  /**
   * Handle bookmarking of searches
   *
   * @memberof RoomReservationComponent
   */
  onSaveSearchClick() {
    const dict: { [key: string]: any } = [];

    this.mapForm(dict, 'building');
    this.mapForm(dict, 'equipments');
    this.mapForm(dict, 'floor');
    this.mapForm(dict, 'location');
    this.mapForm(dict, 'number');
    this.mapForm(dict, 'roomType');
    this.mapForm(dict, 'seats');
    this.mapForm(dict, 'showAvailable');
    this.mapForm(dict, 'showAvailableHours');
    this.mapForm(dict, 'showAvailableTimeFrom');
    this.mapForm(dict, 'showAvailableTimeTo');
    this.mapForm(dict, 'showAvailableType');

    this._bookmarkSaveDialog.onSave(this.searchForm.formGroup.value);
  }

  private mapForm(dict: { [key: string]: any }, element: keyof IRoomSearchForm) {
    dict[element] = this.searchForm.get(element).value;
  }

  /**
   * Handle click on search
   *
   * @memberof RoomReservationComponent
   */
  onSearchClick() {
    // broadcast form value to listeners for search data processing
    this.searchTermsSvc.beforeFormSubmit.next(this.searchFormGroup.value);
    this.executeSearch();
  }

  /**
   * Handle click on reset button
   *
   * @memberof RoomReservationComponent
   */
  onResetClick() {
    const partialFormValues = {
      showExtendedSearch: false,
      showAvailableType: AvailabilityType.absolut,
      showAvailable: AppConfigService.settings.ShowAvailableRoomsChecked,
      number: '',
      idRoom: '',
      location: '',
      roomType: '',
      floor: '',
      equipments: '',
      seats: ''
    };

    this.searchFormGroup.reset();

    this.searchFormGroup.patchValue(partialFormValues);

    this.applyUserSettings();
  }

  /**
   * After reservation saved hook
   *
   * @param {string} location
   * @memberof RoomReservationComponent
   */
  onAfterReservationSaved(location: string) {
    // open confirmation
    const parts = location.split('/');
    this.router.navigate(['/my/confirmation/' + Number(parts[parts.length - 1])]);
  }

  /**
   * Map search form data to search-params object
   *
   * @private
   * @returns {IAvailableRoomsParams}
   * @memberof RoomReservationComponent
   */
  private getAvailableRoomsParams(): IAvailableRoomsParams {
    const params: IAvailableRoomsParams = {
      availableFrom: DateHelper.parseIsoDateString(this.searchForm.getValue('date')),
      availableTo: DateHelper.parseIsoDateString(this.searchForm.getValue('date')),
      availableOnly: !!this.searchForm.getValue('showAvailable'),
      timespan: !!this.searchForm.getValue('showAvailable') ? Number(this.searchForm.getValue('showAvailableHours')) : null,
      ressourceNumber: this.searchForm.getValue('number')
        .split(FORM_VALUE_SEPARATOR)
        .map(r => r.trim()),
      idRoom: this.searchForm.getValue('idRoom')
        .split(FORM_VALUE_SEPARATOR)
        .map(r => Number(r.trim()))
        .filter(n => n !== 0),
      locations: this.selectedLocations
        .map(x => Number(x.trim()))
        .filter(n => n !== 0),
      buildings: this.selectedBuildings
        .map(x => Number(x.trim()))
        .filter(n => n !== 0),
      roomTypes: this.searchForm.getValue('roomType')
        .split(FORM_VALUE_SEPARATOR)
        .filter(x => !StringHelper.isNullOrEmpty(x))
        .map(x => Number(x.trim()))
        .filter(n => n !== 0),
      floors: this.searchForm.getValue('floor')
        .split(FORM_VALUE_SEPARATOR)
        .map(x => x.trim()),
      equipments: this.searchForm.getValue('equipments')
        .split(FORM_VALUE_SEPARATOR)
        .filter(x => !StringHelper.isNullOrEmpty(x))
        .map(x => x.trim()),
      seats: this.searchForm.getValue('seats').split(FORM_VALUE_SEPARATOR).map(x => x.trim())
    };

    if (this.searchForm.getValue('showAvailable') && this.searchForm.getValue('showAvailableType') === AvailabilityType.absolut) {
      params.timespan = null;
      const showAvailableTimeFrom = Number(this.searchForm.getValue('showAvailableTimeFrom'));
      const showAvailableTimeTo = Number(this.searchForm.getValue('showAvailableTimeTo'));

      if (showAvailableTimeFrom !== NaN && showAvailableTimeTo !== NaN) {
        params.availableFrom.setHours(Math.floor(showAvailableTimeFrom / 60), showAvailableTimeFrom % 60, 0, 0);

        const to = showAvailableTimeTo / 60;
        params.availableTo.setHours(Math.floor(to), showAvailableTimeTo % 60, 0, 0);
        if (to === 24) {
          params.availableTo = new Date(params.availableTo.getTime() - 1000); // remove one sec (ie fix)
        }
      }

    } else {
      params.availableFrom.setHours(0, 0, 0);
      params.availableTo.setHours(23, 59, 59, 999);
    }

    return params;
  }

  /**
   * handle loading of bookmarks
   *
   * @param {IBookmarkListItem} item
   * @memberof RoomReservationComponent
   */
  onBookmarkSelect(item: IBookmarkListItem) {
    for (const key in item.settings) {
      if (item.settings.hasOwnProperty(key)) {
        this.searchForm.formGroup.get(key).setValue(item.settings[key]);
      }
    }

    // set date to today
    this.searchForm.setValue('date', DateHelper.getCurrentDate());
    this.updateBuildings();
  }

  onToggleExtendedSearch() {
    const input = this.searchForm.get('showExtendedSearch');
    input.patchValue(!input.value);
  }

  /**
  * Handel openRoomInfo callback of room-allocation-item
  *
  * @param {number} roomId
  * @memberof RoomAllocationComponent
  */
  onOpenSchedule(info: IRoomScheduleInfo) {
    this.activeItemId = info.roomId;
    this.activeRessourceInfo = info;
    this.translate.get('ROOM').subscribe((text: string) => {
      this.modalRoomSchedule.title = text + ' ' + info.designation;
    });
    this.modalRoomSchedule.openModal();
  }

  /**
    * Query the server for availableRooms
    *
    * @private
    * @memberof RoomReservationComponent
    */
  protected executeSearch() {

    this.isLoading = true;

    // open panel (setTimeout: workaround for animation issue in IE and Edge)
    window.setTimeout(() => { this.accordion.openSingle(this.accordionResultPanel); }, 1);

    const params = this.getAvailableRoomsParams();
    this.currentDate = params.availableFrom;

    const list = new Array<AvailableRoom>();
    this.room.getAvailableRooms(params)
      .subscribe((data: AvailableRoom[]) => {

        (data as AvailableRoom[]).forEach(r => {
          const occs = new Array<Occupancy>();
          if (r.Occupancies != null) {
            r.Occupancies.forEach(o => {
              occs.push(Occupancy.create(o));
            });
          }

          const obj = AvailableRoom.create(r);
          obj.Occupancies = occs;
          obj.meta = this.room.getReservabilityToken(obj, this.currentDate);

          // subscribe warnings (mainly required because of translation)
          const observer = this.room.getFirstWarning(obj, this.currentDate);
          if (observer != null) {
            observer.subscribe((message: string) => {
              obj.warning = message;
            });
          }

          list.push(obj);
        });

        // assign prepared data
        this.allocations = list;

        this.allocationTableModel = this.mapAllocationTableModel(
          this.allocations,
          DateHelper.StartOfDay(this.currentDate),
          DateHelper.EndOfDay(this.currentDate)
        );

        this.isLoading = false;
      },
        (error: HttpErrorResponse) => {
          console.error('error on loading result', error);

          this.translate.get('DATA_LODING_ERROR_GENERAL').subscribe(
            (text: string) => { this.messages.add(text, MessageType.Error); }
          );

          this.isLoading = false;
        });

  }

  /**
   * Create AllocationTableModel from AvailableRoom array
   *
   * @param {AvailableRoom[]} rooms
            * @param {Date} minDate
            * @param {Date} maxDate
            * @returns {AllocationTableModel}
            * @memberof RoomReservationComponent
            */
  mapAllocationTableModel(rooms: AvailableRoom[], minDate: Date, maxDate: Date): AllocationTableModel {
    const model = new AllocationTableModel();
    model.resourceType = ResourceType.Raum;

    // header row generation
    model.additionalColums = RoomService.selectVisibleFields(null);

    this.translate.get('ROOM_CALENDAR').subscribe((text: string) => {
      model.calendarButtonText = text;
    });

    this.translate.get('DEVICE').subscribe((text: string) => {
      model.mainColumnTitle = text;
    });

    model.displayItems = rooms.map(ar => {
      const di = AvailableRoom.mapDisplayItem(ar, this.translate.get('OCCUPIED'), minDate, maxDate);
      // multi data row generation
      di.additionalColumsData = RoomService.selectVisibleFields(ar);

      return di;
    });

    return model;
  }

  collapseChanedReactor(data: { id: string, isExpanded: boolean }) {
    this.isSearchPanelExpaneded = data.isExpanded;
  }

  /**
   * Apply url-params that do not depend on any other controls
   *
   * @private
   * @returns
   * @memberof RoomReservationComponent
   */
  private applyPrimaryUrlParams() {
    // get params
    const params = UrlHelper.fetchReservationUrlParams(this.activatedRoute);
    if (!params) {
      return;
    }

    // if not provided, do not touch default
    if (params.date) {
      this.searchForm.setValue('date', String(params.date));
    }

    if (params.fct && params.fct.toUpperCase() === 'SHOWCALENDAR') {

      if (!params.idRoom || !params.idRoom.length || !params.date) {
        console.error('function "' + params.fct + '" requires "' + nameof<ISearchUrlParams>('date')
          + '" and "' + nameof<ISearchUrlParams>('idRoom') + '" to be set');
        return;
      }

      this.activeItemId = params.idRoom[0];
      window.setTimeout(() => { this.modalRoomSchedule.openModal(); }, 200);
      return;
    }

    // actually not in form
    // todo: remove id support and change to room numbers
    this.searchForm.setValue('idRoom', params.idRoom ? params.idRoom.join(FORM_VALUE_SEPARATOR) : '');

    this.searchForm.setValue('number', params.roomNumber ? params.roomNumber.join(FORM_VALUE_SEPARATOR) : '');

    if (params.timeslot || params.timeFrom || params.timeTo) {
      this.searchForm.setValue('showAvailable', true);

      // timespan
      if (params.timeslot) {
        this.searchForm.setValue('showAvailableType', AvailabilityType.span);
        this.searchForm.setValue('showAvailableHours', Math.round(params.timeslot));
      }

      // time from/to
      if (params.timeFrom || params.timeTo) {
        this.searchForm.setValue('showAvailableType', AvailabilityType.absolut);

        const from = params.timeFrom ? DateHelper.getMinutesOfDay(params.timeFrom) : (DateHelper.getMinutesOfDay(params.timeTo) - 60);
        this.searchForm.setValue('showAvailableTimeFrom', from);

        const to = params.timeTo ? DateHelper.getMinutesOfDay(params.timeTo) : (DateHelper.getMinutesOfDay(params.timeFrom) + 60);
        this.searchForm.setValue('showAvailableTimeTo', to);
      }
    } else {
      this.searchForm.setValue('showAvailable', false);
      this.searchForm.setValue('showAvailableType', AvailabilityType.absolut);
      this.searchForm.setValue('showAvailableTimeFrom', 0);
      this.searchForm.setValue('showAvailableTimeTo', 0);
      this.searchForm.setValue('showAvailableHours', 0);
    }

    // open/close extended options
    this.detailSearchEnabled = !!(
      params.idLocation || params.idBuilding || params.roomType || params.floor || params.equipment || params.capacity
    );
    this.searchForm.setValue('showExtendedSearch', this.detailSearchEnabled);

    this.searchForm.setValue('location', params.idLocation ? params.idLocation.join(FORM_VALUE_SEPARATOR) : '');
    this.searchForm.setValue('building', params.idBuilding ? params.idBuilding.join(FORM_VALUE_SEPARATOR) : '');

    // trigger manually if combination of params does not lead to invacation of value-change of 'building'
    if (!this.detailSearchEnabled || !params.idBuilding) {
      this.applySecondaryUrlParams();
    }
  }

  private applySecondaryUrlParams() {
    this.initialRun = false;

    // get params
    const params = UrlHelper.fetchReservationUrlParams(this.activatedRoute);
    if (!params) {
      return;
    }

    if (params.fct && params.fct.toUpperCase() === 'SHOWCALENDAR') {
      return;
    }
    this.searchForm.setValue('roomType', params.roomType ? params.roomType.join(FORM_VALUE_SEPARATOR) : '');
    this.searchForm.setValue('floor', params.floor ? params.floor.join(FORM_VALUE_SEPARATOR) : '');
    this.searchForm.setValue('equipments', params.equipment ? params.equipment.join(FORM_VALUE_SEPARATOR) : '');
    this.searchForm.setValue('seats', params.capacity ? params.capacity.join(FORM_VALUE_SEPARATOR) : '');

    if (params.startSearch) {
      window.setTimeout(() => { this.executeSearch(); }, 200);
    }

  }

}
